// Analytics module
//
// Central event pipeline with a sink registry and a pre-consent buffer:
// - track() is safe to call from page load. Vendors (Amplitude, GA4, Meta Pixel)
//   register a sink via registerAnalyticsSink() once their consent category is
//   granted; events captured before a sink existed are replayed to it in order,
//   so nothing fired while consent was pending (geo lookup, EU banner) is lost.
// - Privacy: pre-consent events live ONLY in memory — no storage writes, no
//   network. Persistent ids (user_id in localStorage, session_id in
//   sessionStorage) belong to the ANALYTICS consent category: they are minted
//   only after main.ts calls enableAnalyticsIdentity() (analytics granted).
//   Under marketing-only consent the Meta sink receives events WITHOUT those
//   ids — same as the pre-overhaul fbq behavior. If the user declines
//   everything, main.ts calls clearAnalyticsBuffer() and the buffer is dropped.
import { isDevelopment } from '../utils/environment';

// --- Sink registry + pre-consent buffer -------------------------------------

const MAX_BUFFERED_EVENTS = 200;

let eventBuffer = [];      // [{ event, properties }] — properties carry capture-time timestamp
let bufferOverflowed = false;
let bufferingDisabled = false; // set when consent is declined — stop accumulating
const sinks = [];          // [{ name, fn(eventName, properties) }]
const eventEnrichers = []; // [fn() => props] — capture-time property providers (e.g. A/B arms)

/**
 * Register a capture-time property provider: its output is merged into every
 * event's properties at track() time (explicit properties win). Runs before
 * buffering, so pre-consent events replay to late sinks already enriched.
 * Unlike sinks, enrichers are consent-free — register only functional,
 * non-identifying context (e.g. experiment arms).
 */
export function registerEventEnricher(fn) {
  eventEnrichers.push(fn);
}

/**
 * Register a vendor sink. Call ONLY after the vendor's consent category is
 * granted — registration mints persistent analytics ids (localStorage/
 * sessionStorage) and replays the buffered pre-consent events to the new sink.
 */
export function registerAnalyticsSink(name, fn) {
  if (sinks.some(s => s.name === name)) return;
  sinks.push({ name, fn });

  // Replay everything captured before this sink existed (original timestamps
  // are preserved in event.properties.timestamp).
  for (const event of eventBuffer) {
    enrichEvent(event);
    dispatchToSink({ name, fn }, event);
  }
  if (isDevelopment() && eventBuffer.length > 0) {
    console.log(`📊 Analytics: replayed ${eventBuffer.length} buffered events to sink "${name}"`);
  }
}

/**
 * Drop the in-memory pre-consent buffer and stop buffering. Called when the
 * user declines all non-essential consent — the events must never be sent.
 */
export function clearAnalyticsBuffer() {
  eventBuffer = [];
  bufferingDisabled = true;
}

// Persistent analytics ids are gated on the ANALYTICS consent category, not on
// mere sink presence: a marketing-only grant (Meta sink) must not mint
// localStorage/sessionStorage identifiers the user explicitly declined.
let identityEnabled = false;

/**
 * Allow minting/stamping of persistent analytics ids (localStorage user_id,
 * sessionStorage session_id). Call ONLY when the analytics category is granted.
 */
export function enableAnalyticsIdentity() {
  identityEnabled = true;
}

function dispatchToSink(sink, event) {
  try {
    sink.fn(event.event, event.properties);
  } catch (error) {
    console.error(`Analytics sink "${sink.name}" failed:`, error);
  }
}

// Stamp ids onto an event once, at dispatch time (post-consent). Idempotent —
// buffered events are shared across sink replays. No-op until the analytics
// category is granted (see enableAnalyticsIdentity).
function enrichEvent(event) {
  if (!identityEnabled) return;
  if (!event.properties.session_id) {
    event.properties.session_id = getSessionId();
  }
  if (event.event === 'session_start' && !event.properties.user_id) {
    event.properties.user_id = getOrCreateUserId();
  }
}

// --- Core init (consent-free) ------------------------------------------------

let analyticsCoreInitialized = false;

/**
 * Consent-free analytics core — runs at boot, BEFORE the consent gate.
 * Only touches memory: fires session_start into the buffer at real page-load
 * time and registers visibility listeners. Vendors attach later via
 * registerAnalyticsSink() once consent is granted.
 */
export function initAnalytics() {
  if (analyticsCoreInitialized) return;
  analyticsCoreInitialized = true;

  // Track session start (user_id is stamped at dispatch time, post-consent)
  track('session_start', {
    url: window.location.href,
    referrer: document.referrer,
    user_agent: navigator.userAgent,
    screen_size: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`
  });

  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      track('app_background');
    } else {
      track('app_foreground');
    }
  });
}

// Track event
export function track(eventName, properties = {}) {
  const enriched = {};
  for (const enricher of eventEnrichers) {
    try {
      Object.assign(enriched, enricher());
    } catch (error) {
      console.error('Analytics event enricher failed:', error);
    }
  }

  const event = {
    event: eventName,
    properties: {
      ...enriched,
      ...properties,
      timestamp: new Date().toISOString()
    }
  };

  // Log to console in development
  if (isDevelopment()) {
    console.log('📊 Analytics:', event);
  }

  // Buffer for sinks that register later (bounded; memory only)
  if (!bufferingDisabled) {
    eventBuffer.push(event);
    if (eventBuffer.length > MAX_BUFFERED_EVENTS) {
      eventBuffer.shift();
      if (!bufferOverflowed) {
        bufferOverflowed = true;
        console.warn(`Analytics buffer exceeded ${MAX_BUFFERED_EVENTS} events — oldest events won't replay to late sinks`);
      }
    }
  }

  // Dispatch to registered sinks (only possible post-consent)
  if (sinks.length > 0) {
    enrichEvent(event);
    for (const sink of sinks) {
      dispatchToSink(sink, event);
    }
    sendToEndpoint(event);
  }
}

// Send to analytics endpoint
async function sendToEndpoint(event) {
  try {
    // Batch events
    addToBatch(event);

    // Send batch after delay
    scheduleBatchSend();
  } catch (error) {
    console.error('Failed to track event:', error);
  }
}

// Event batching
let eventBatch = [];
let batchTimer = null;

function addToBatch(event) {
  eventBatch.push(event);

  // Send immediately if batch is large
  if (eventBatch.length >= 10) {
    sendBatch();
  }
}

function scheduleBatchSend() {
  if (batchTimer) return;

  batchTimer = setTimeout(() => {
    sendBatch();
    batchTimer = null;
  }, 2000); // Send after 2 seconds
}

async function sendBatch() {
  if (eventBatch.length === 0) return;

  const events = [...eventBatch];
  eventBatch = [];

  try {
    // Send to your analytics endpoint
    // await fetch('/api/analytics', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ events })
    // });
  } catch (error) {
    console.error('Failed to send analytics batch:', error);
    // Re-add events to batch for retry
    eventBatch.unshift(...events);
  }
}

// Get or create user ID
function getOrCreateUserId() {
  const key = 'lunai_analytics_user_id';
  let userId = localStorage.getItem(key);

  if (!userId) {
    userId = 'u_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(key, userId);
  }

  return userId;
}

// Get or create session ID
function getSessionId() {
  const key = 'lunai_session_id';
  const sessionDuration = 30 * 60 * 1000; // 30 minutes

  let session = JSON.parse(sessionStorage.getItem(key) || 'null');
  const now = Date.now();

  if (!session || now - session.lastActivity > sessionDuration) {
    session = {
      id: 's_' + now + '_' + Math.random().toString(36).substr(2, 9),
      startTime: now,
      lastActivity: now
    };
  } else {
    session.lastActivity = now;
  }

  sessionStorage.setItem(key, JSON.stringify(session));
  return session.id;
}

// Track specific events
export function trackScreenView(screenName, properties = {}) {
  track('screen_view', {
    screen_name: screenName,
    ...properties
  });
}

export function trackButtonClick(buttonName, properties = {}) {
  track('button_click', {
    button_name: buttonName,
    ...properties
  });
}

export function trackFormSubmit(formName, properties = {}) {
  track('form_submit', {
    form_name: formName,
    ...properties
  });
}

export function trackCheckoutStart(properties = {}) {
  track('checkout_start', properties);
}

export function trackPurchase(properties = {}) {
  track('purchase', properties);
}

// Listen for analytics events from other modules
document.addEventListener('analytics:screen', (event) => {
  trackScreenView(event.detail.screen_name, event.detail);
});
